import { Stack } from "@mantine/core";
import ItemCard from "@/components/ItemCard";
import GhostCard from "@/components/GhostCard";
import SeeMoreCard from "@/components/SeeMoreCard";
import BaseCarousel from "@/components/BaseCarousel";

import styles from "./styles.module.scss";

type SeeMoreCard = {
  title: string;
  description?: string;
  link: string;
};

type Props = {
  items?: SearchResultItem[];
  loading?: boolean;
  ghosts?: number;
  routeToProductPage?: boolean;
  seeMoreCard?: SeeMoreCard;
  useWrapper?: boolean;
  title?: string;
  seeMoreLink?: string;
  seeMoreText?: string;
  /** Edge-to-edge mode for use in slide-ins */
  edgeToEdge?: boolean;
  /** Render four items per mobile carousel page as a 2x2 grid */
  mobileGrid?: boolean;
  /** Reserve the fourth slot on the final mobile page for See More */
  reserveMobileSeeMoreSlot?: boolean;
  /** Limit the number of product cards rendered in the mobile carousel */
  mobileItemLimit?: number;
  /** Optional class for a carousel embedded in a constrained surface */
  className?: string;
  /** Render a single desktop row of compact square image cards */
  desktopCompact?: boolean;
  /** Keep navigation controls inside a constrained parent container */
  insetNavButtons?: boolean;
};

const ItemCarousel = ({
  items,
  loading,
  ghosts = 20,
  routeToProductPage,
  seeMoreCard,
  useWrapper = true,
  title,
  seeMoreLink,
  seeMoreText,
  edgeToEdge,
  mobileGrid = true,
  reserveMobileSeeMoreSlot = false,
  mobileItemLimit,
  className,
  desktopCompact = false,
  insetNavButtons = false,
}: Props) => {
  if (!loading && !items?.length) {
    return null;
  }

  const GhostCards = Array.from({ length: ghosts }).map((_, i) => (
    <GhostCard key={`ghost-${i}`} index={i} imageOnly={desktopCompact} />
  ));

  const cards =
    loading || !items?.length
      ? GhostCards
      : items?.map((item) => (
          <ItemCard
            key={item.id}
            item={item}
            ignoreResponsive
            imageOnly={desktopCompact}
            routeToProductPage={routeToProductPage}
          />
        ));

  const seeMore =
    seeMoreCard && !loading ? (
      <SeeMoreCard
        key="see-more-card"
        {...seeMoreCard}
        mobileGridPlacement={mobileGrid && reserveMobileSeeMoreSlot}
      />
    ) : null;

  const carouselItems = [...cards, ...(seeMore ? [seeMore] : [])];
  const mobileCarouselItems =
    mobileItemLimit === undefined
      ? carouselItems
      : [...cards.slice(0, mobileItemLimit), ...(seeMore ? [seeMore] : [])];
  const mobileContent = mobileGrid ? (
    <>
      {Array.from({ length: Math.ceil(mobileCarouselItems.length / 4) }, (_, pageIndex) => {
        const pageItems = mobileCarouselItems.slice(pageIndex * 4, pageIndex * 4 + 4);
        const hasSeeMore = Boolean(seeMore && pageItems.includes(seeMore));
        const incomplete = pageItems.length < 4 && mobileCarouselItems.length >= 4 && !hasSeeMore;

        return (
          <Stack
            key={`mobile-grid-page-${pageIndex}`}
            className={styles.mobileGridPage}
            data-mobile-grid-page="true"
            data-mobile-grid-incomplete={incomplete || undefined}
            gap={0}
          >
            {pageItems}
          </Stack>
        );
      })}
    </>
  ) : (
    <>{mobileCarouselItems}</>
  );
  const content =
    mobileItemLimit === undefined ? (
      mobileContent
    ) : (
      <>
        <Stack className={styles.mobileCarouselContent} data-carousel-mobile-content="true" gap={0}>
          {mobileContent}
        </Stack>
        <Stack
          className={styles.desktopCarouselContent}
          data-carousel-desktop-content="true"
          gap={0}
        >
          {carouselItems}
        </Stack>
      </>
    );

  if (!useWrapper) {
    return content;
  }

  return (
    <BaseCarousel
      title={title}
      seeMoreLink={seeMoreLink}
      seeMoreText={seeMoreText}
      wrapperClassName={styles.itemContainer}
      className={`${styles.imageAlignedNav} ${className || ""} ${
        desktopCompact ? styles.desktopCompact : ""
      }`.trim()}
      scrollAmount={desktopCompact ? "page" : 320}
      showNavButtons={!!title}
      edgeToEdge={edgeToEdge}
      insetNavButtons={desktopCompact || insetNavButtons}
    >
      {content}
    </BaseCarousel>
  );
};

export default ItemCarousel;
